import { getPollOptionLabels } from './pollOptions'
import keys from 'constants/keys'
import dayjs from 'dayjs'
import { FrequencyType } from 'components/NewsFeed/PollTaskForm/utils'
import { isNil, omit } from 'lodash'
import { Content, Subtask, TaskAudience, TaskFormData, TaskTimeFrame } from './types'
import { calculateDaysOfWeek, calculateRecurrenceDeadline, calculateRecurrenceFrequency, calculateTots } from '../utils'

/**
 * Finds the next valid date based on the given `from_ts` and `days_of_week`.
 * @param from_ts - The original timestamp (string or dayjs object).
 * @param days_of_week - Array of valid day indices (0 - Sunday, 6 - Saturday).
 * @returns A dayjs object with the updated date.
 */
function getNextValidDate(from_ts: string, days_of_week: number[]): dayjs.Dayjs {
  const fromDate = dayjs(from_ts)
  const currentDayIndex = fromDate.day()

  // Check if the current day index is in the allowed days
  if (days_of_week.includes(currentDayIndex)) {
    return fromDate // Return original date if it's valid
  }

  // Find the next valid day
  const nextValidDayIndex = days_of_week.find((day) => day > currentDayIndex) ?? days_of_week[0]

  // Calculate the difference in days
  const daysToAdd =
    nextValidDayIndex > currentDayIndex ? nextValidDayIndex - currentDayIndex : 7 - currentDayIndex + nextValidDayIndex

  // Return the updated date
  return fromDate.add(daysToAdd, 'day')
}

export function resolveTaskStartDate({
  from_ts,
  is_recurring,
  recurrence_frequency,
  days_of_week,
  monthly_range,
}: TaskTimeFrame) {
  if (!is_recurring) {
    return from_ts
  }
  if (recurrence_frequency === FrequencyType.DAY) {
    if (days_of_week?.length === 7) {
      return from_ts
    }
    return getNextValidDate(from_ts, days_of_week ?? [])
  }
  if (recurrence_frequency === FrequencyType.WEEK && days_of_week) {
    const fromDate = dayjs(from_ts)
    const currentDayIndex = fromDate.day()

    const startDay = days_of_week[0] // First day in the range
    const endDay = days_of_week[days_of_week.length - 1] // Last day in the range

    // Case 1: If `currentDayIndex` is within the range (including boundaries)
    if (
      (startDay <= endDay && currentDayIndex >= startDay && currentDayIndex <= endDay) || // Normal range
      (startDay > endDay && (currentDayIndex >= startDay || currentDayIndex <= endDay)) // Cyclic range
    ) {
      // Always return the startDay (adjusted to the previous week if needed)
      const daysToSubtract = currentDayIndex >= startDay ? currentDayIndex - startDay : 7 - (startDay - currentDayIndex)

      const adjustedDate = fromDate.subtract(daysToSubtract, 'day')
      return adjustedDate.hour(fromDate.hour()).minute(fromDate.minute()).second(fromDate.second())
    }

    // Case 2: If `currentDayIndex` is not within range
    const nextDay = days_of_week.find((day) => day > currentDayIndex) ?? days_of_week[0]
    const daysToAdd = nextDay > currentDayIndex ? nextDay - currentDayIndex : 7 - currentDayIndex + nextDay // Cycle to the next week

    const nextValidDay = fromDate.add(daysToAdd, 'day')
    return nextValidDay.hour(fromDate.hour()).minute(fromDate.minute()).second(fromDate.second())
  }

  if (recurrence_frequency === FrequencyType.MONTH) {
    const datePart = dayjs(monthly_range[0])
    const timePart = dayjs(from_ts)
    return datePart.hour(timePart.hour()).minute(timePart.minute()).second(timePart.second())
  }
}

export function separateTaskContents(subtasks: (Subtask | Content)[]) {
  const contents: Content[] = []
  const regularSubtasks: Subtask[] = []
  let persistedIndex = 0

  subtasks.forEach((subtask) => {
    if (!('index_ref' in subtask)) {
      regularSubtasks.push(subtask)
      persistedIndex++
      return
    }

    const rest = omit(subtask, 'type')
    const content = {
      ...rest,
      text: subtask.text?.trim() || null,
      rich_text: subtask.rich_text || null,
      files: subtask.files?.length ? subtask.files : null,
      images: subtask.images?.length ? subtask.images : null,
      videos: subtask.videos?.length ? subtask.videos : null,
      // Derive the insertion point from the combined order that will actually be persisted.
      // Empty Info boxes disappear, so they must not leave gaps that move later blocks.
      index_ref: persistedIndex,
    }

    if (
      [content.text, content.rich_text, content.files, content.images, content.videos].some((value) => value !== null)
    ) {
      contents.push(content as Content)
      persistedIndex++
    }
  })
  return { contents, regularSubtasks }
}

export function normalizeSubmissionSubtasks(
  subtasks: Subtask[],
  disableGalleryUpload: boolean,
  aiEnabled: boolean
): Subtask[] {
  return subtasks.map((source, index) => {
    let subtask = { ...source, order_index: index }

    if (source.type === keys.SUBTASK_TYPES.IMAGE_SUBTASK || source.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS) {
      subtask = { ...subtask, disable_gallery_upload: disableGalleryUpload }
    }
    if (source.type === keys.SUBTASK_TYPES.POLL_SUBTASK || source.type === keys.SUBTASK_TYPES.PRIVATE_POLL) {
      subtask.options = getPollOptionLabels(source.options)
    }
    if (!aiEnabled || !subtask.is_ai_enhanced) {
      return omit(subtask, ['is_ai_enhanced', 'ai_prompt', 'prompt_id', 'reference_images'])
    }
    return subtask
  })
}

export function cleanSubtaskPayload(subtask: Subtask): Subtask {
  const { message_id } = subtask
  const rest = omit(subtask, ['message_id', 'ai_prompt'])
  const base = message_id ? { ...rest, message_id } : rest

  if (!base.content) return base

  const content = omit(base.content, 'images_base64')

  return { ...base, content }
}

/** A numeric zero is a deliberate selection, not an empty field. */
export function resolveAllowedOverdueDays(
  timeFrameValue: number | string | null | undefined,
  settingsValue: number
): number {
  return Number(
    timeFrameValue !== null && timeFrameValue !== undefined && timeFrameValue !== '' ? timeFrameValue : settingsValue
  )
}

export function buildTaskSubmission(
  allData: Partial<TaskFormData>,
  options: { utcOffset: number; aiEnabled: boolean; groupedByCategory: boolean }
) {
  const { title, sub_tasks } = allData?.SUBTASK_TASK_CREATION as {
    title: string
    sub_tasks: (Subtask | Content)[]
  }

  const { groups, users, tags } = allData?.TASK_SETTINGS?.audience as TaskAudience

  const {
    is_individual_results,
    send_schedule_push,
    view,
    from_ts: taskSettingsFromTs,
    deadline: taskSettingsDeadline,
    allowed_overdue_days,
    priority,
    disable_gallery_upload,
    rework_limit,
    allow_completion_after_deadline,
    watchers,
    auditors,
    type,
  } = allData?.TASK_SETTINGS as TaskFormData['TASK_SETTINGS']

  const {
    from_ts = taskSettingsFromTs,
    deadline = taskSettingsDeadline,
    frequency_time = '',
    monthly_range = [],
    is_recurring = false,
    days_of_week = [],
    allowed_overdue_days: timeFrameAllowedOverdueDays,
    recurrence_frequency = null,
    review_to_ts = null,
  } = allData?.TIME_FRAME_SELECTION || {}

  const formattedFromTs = dayjs(
    resolveTaskStartDate({
      from_ts,
      deadline,
      frequency_time,
      monthly_range,
      is_recurring,
      days_of_week,
      allowed_overdue_days: timeFrameAllowedOverdueDays ?? allowed_overdue_days,
      recurrence_frequency,
    })
  ).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  const formattedDeadline = dayjs(deadline).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP)
  const formattedReviewToTs = review_to_ts ? dayjs(review_to_ts).format(keys.DEFAULT_STRING_FORMAT_TIMESTAMP) : null
  const recurrence_deadline = calculateRecurrenceDeadline({
    is_recurring,
    recurrence_frequency,
    from_ts: formattedFromTs,
    frequency_time,
    monthly_range,
    days_of_week,
  })
  const { contents, regularSubtasks } = separateTaskContents(sub_tasks)
  const normalizedSubtasks = normalizeSubmissionSubtasks(regularSubtasks, disable_gallery_upload, options.aiEnabled)
  const isVMTaskPayload = type === keys.TASK_V2_TYPES.VM_TASK
  const editTaskPayload = {
    utc_offset: options.utcOffset,
    send_schedule_push,
    updated_to_ts: calculateTots({ is_recurring, deadline, formattedDeadline }),
    updated_review_to_ts: formattedReviewToTs,
    updated_deadline: formattedDeadline.toString(),
    updated_task_users: users,
    updated_task_groups: groups,
    updated_task_tags: tags,
    task_data: {
      type: type,
      title,
      view: view!,
      allowed_overdue_days: resolveAllowedOverdueDays(timeFrameAllowedOverdueDays, allowed_overdue_days),
      priority: priority,
      ...(!isNil(rework_limit) ? { rework_limit: Number(rework_limit) } : {}),
      ...(contents.length > 0 ? { contents } : {}),
      watchers: watchers || [],
      ...(isVMTaskPayload ? { auditors: auditors || [] } : {}),
    },
  }

  const data = {
    title,
    is_recurring,
    recurrence_frequency: calculateRecurrenceFrequency({
      frequency: recurrence_frequency,
      days_of_week: days_of_week,
    }),
    recurrence_interval: is_recurring ? 1 : null,
    recurrence_days_of_week: calculateDaysOfWeek({ recurrence_frequency, days_of_week }),
    recurrence_start_time: is_recurring ? formattedFromTs.slice(8) : null,
    view,
    categories: [],
    type: type,
    is_individual_results,
    allowed_overdue_days: resolveAllowedOverdueDays(timeFrameAllowedOverdueDays, allowed_overdue_days),
    priority: priority,
    ...(!isNil(rework_limit) ? { rework_limit: Number(rework_limit) } : {}),
    allow_completion_after_deadline,
    watchers: watchers || [],
    ...(isVMTaskPayload ? { auditors: auditors || [] } : {}),
    ...(contents.length > 0 ? { contents } : {}),
    ...(options.groupedByCategory ? { is_categories_required: true } : {}),
  }

  const createTaskPayload = {
    from_ts: formattedFromTs,
    to_ts: calculateTots({ is_recurring, deadline, formattedDeadline }),
    review_to_ts: formattedReviewToTs,
    deadline: formattedDeadline.toString(),
    recurrence_deadline,
    groups,
    users,
    tags,
    utc_offset: options.utcOffset,
    send_schedule_push,
    send_campaign_start_push: true,
    data,
    sub_tasks: normalizedSubtasks,
  }

  return { createTaskPayload, editTaskPayload }
}
